'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useDecisions, useLoyaltyContext, usePageLayout } from '../hooks';
import type { MedalliaSDK } from '../scripts';
import { useConsentCategory } from '../scripts/hooks';
import type { SubscriptionOrder } from '../types';
import { buildExtras, buildPetsmartMedalliaContextFromSubscription } from './buildPetsmartMedalliaContext';
import type { AutoshipActionType, PetsmartMedalliaSubscriptionActionInfo } from './types';
import { useGetActiveOptimizelyCampaigns } from './useGetActiveOptimizelyCampaigns';

// Sonar (S7764) requires globalThis over window; the Window cast keeps the global
// augmentations from window.d.ts (petsmartMedalliaContext, KAMPYLE_ONSITE_SDK) typed.
const globalScope = globalThis as typeof globalThis & Window;
const SURVEY_CLOSE_POLL_INTERVAL_MS = 100;
const SURVEY_CLOSE_MAX_WAIT_MS = 15_000;
// Matches useMedalliaForm / useMedalliaFormV2 — if neb_OnsiteLoaded never fires (blocked
// script, network failure), the returned promise still resolves so an awaiting caller is
// never stuck.
const SDK_LOAD_TIMEOUT_MS = 10_000;

// The SDK has no close callback (confirmed against the live KAMPYLE_ONSITE_SDK surface — showForm
// doesn't invoke anything on close), so isSurveyDisplayed() is polled instead. Resolves once a
// displayed survey disappears, or after a max wait if it never displays at all — either way the
// caller is never left stuck.
function waitForSurveyClose(sdk: MedalliaSDK, onClose: () => void): () => void {
  let hasBeenDisplayed = false;
  try {
    hasBeenDisplayed = sdk.isSurveyDisplayed();
  } catch {
    // Can't observe the overlay — resolve immediately so an awaiting caller is not stuck.
    onClose();
    return () => {};
  }
  let elapsedMs = 0;
  const intervalId = setInterval(() => {
    elapsedMs += SURVEY_CLOSE_POLL_INTERVAL_MS;
    try {
      if (sdk.isSurveyDisplayed()) {
        hasBeenDisplayed = true;
        return;
      }
    } catch {
      clearInterval(intervalId);
      onClose();
      return;
    }
    if (hasBeenDisplayed || elapsedMs >= SURVEY_CLOSE_MAX_WAIT_MS) {
      clearInterval(intervalId);
      onClose();
    }
  }, SURVEY_CLOSE_POLL_INTERVAL_MS);
  return () => clearInterval(intervalId);
}
type WriteMedalliaContextParams = Parameters<typeof buildExtras>[0] & {
  hasC0002: boolean;
  isMedalliaEnabled: boolean;
  order: SubscriptionOrder | null | undefined;
  actionInfo?: PetsmartMedalliaSubscriptionActionInfo;
};

// Shared by the base sync effect and recordMedalliaAction — both need to write the same
// window.petsmartMedalliaContext shape (gated on consent + the medallia_custom flag + order),
// just with a different actionInfo. Kept as one function so the two call sites can't drift.
function writeMedalliaContext({
  hasC0002,
  isMedalliaEnabled,
  order,
  actionInfo,
  ...extrasInput
}: WriteMedalliaContextParams): void {
  if (!hasC0002 || !isMedalliaEnabled || !order) {
    delete globalScope.petsmartMedalliaContext;
    return;
  }
  const extras = buildExtras(extrasInput);
  globalScope.petsmartMedalliaContext = buildPetsmartMedalliaContextFromSubscription(order, extras, actionInfo);
}
interface UseMedalliaAutoshipSyncOptions {
  /** Subscription the Medallia context is built from; no context is written while absent. */
  order: SubscriptionOrder | null | undefined;
  /** Medallia form to load/show for surveys; when absent, action surveys fall back to updatePageView. */
  formId?: string;
  /** Per-action form overrides (e.g. a dedicated skip_order survey) — take precedence over `formId` when the triggered action has one. */
  actionFormIds?: Partial<Record<AutoshipActionType, string>>;
}
export type RecordMedalliaActionOptions = {
  /** Item-level subscription the action targeted (skip_item, cancel_subscription). */
  subscriptionId?: string;
  /** Human-readable frequency label, e.g. "4 weeks" — matches subscription_frequency in GTM events. */
  subscriptionFrequency?: string;
  /** Reason selected in the cancellation flow (cancel_subscription only). */
  cancelReason?: string;
  /**
   * Overrides `order.place` for this call only. The `order` this hook was given is a snapshot
   * from before the action's own refetch — for change_date specifically, that snapshot still
   * has the pre-change date at the moment the action is recorded.
   */
  orderDate?: string;
  /** Attempt to display a survey when `success` is true. Defaults to false — most actions just record. */
  showSurvey?: boolean;
};
export interface UseMedalliaAutoshipSyncResult {
  /**
   * Records a completed subscription action into window.petsmartMedalliaContext and, when
   * `success` is true and `options.showSurvey` is set, imperatively shows that action's survey
   * (its own form if `actionFormIds` has one, else the generic `formId`, else a passive
   * updatePageView), resolving once it closes. `showSurvey` defaults to false — most actions
   * just record. The context write and the survey display are one call so Medallia always reads
   * the data for the action whose survey it's about to show — recording and displaying used to
   * be two separate writes (one via a state-driven effect, one imperative) that could race, with
   * the delayed one clobbering the fresher one with stale data.
   * Never rejects — SDK throws from showForm/isSurveyDisplayed settle the promise instead — so a
   * caller can always safely await it without hanging or treating a successful action as a failure.
   */
  recordMedalliaAction: (type: AutoshipActionType, success: boolean, options?: RecordMedalliaActionOptions) => Promise<void>;
}

/**
 * Manages the Medallia survey lifecycle for the Autoship Subscription Manager: keeps
 * window.petsmartMedalliaContext in sync with base order/user/loyalty/campaign data, and
 * returns a callback to record+display an action-specific survey on demand.
 */
export function useMedalliaAutoshipSync({
  order,
  formId,
  actionFormIds
}: UseMedalliaAutoshipSyncOptions): UseMedalliaAutoshipSyncResult {
  const {
    memberPoints
  } = useLoyaltyContext();
  const {
    user,
    userData
  } = usePageLayout();
  const hasC0002 = useConsentCategory('C0002');
  const isMedalliaEnabled = useDecisions('medallia_autoship');
  const isActive = hasC0002 && isMedalliaEnabled;
  const optimizelyCampaigns = useGetActiveOptimizelyCampaigns(isActive);

  // Holds the in-flight action's info and order override (if any) between the moment
  // recordMedalliaAction writes it and the moment it settles (survey shown & closed, or
  // resolved without one). The base sync effect below reads this on every run instead of
  // hardcoding "no action"/the raw order so that if it re-fires mid-flight (order/user data
  // changing while we're waiting on the SDK to load or the survey to close), it reasserts the
  // active action's info AND its order override (e.g. change_date's orderDate) instead of
  // wiping either back to "no action"/the stale pre-action order — Medallia reads
  // window.petsmartMedalliaContext at the moment showForm is called, which can be well after
  // recordMedalliaAction's own write.
  const activeActionRef = useRef<{
    actionInfo: PetsmartMedalliaSubscriptionActionInfo;
    order: SubscriptionOrder | null | undefined;
  } | undefined>(undefined);

  // Keeps window.petsmartMedalliaContext in sync with base order/user/loyalty/campaign data as
  // it changes, carrying forward whichever action (if any) recordMedalliaAction currently has
  // in flight.
  useEffect(() => {
    if (!isActive) {
      delete globalScope.petsmartMedalliaContext;
      return;
    }
    writeMedalliaContext({
      hasC0002,
      isMedalliaEnabled,
      order: activeActionRef.current ? activeActionRef.current.order : order,
      user,
      userData,
      memberPoints,
      optimizelyCampaigns,
      actionInfo: activeActionRef.current?.actionInfo
    });
    return () => {
      delete globalScope.petsmartMedalliaContext;
    };
  }, [isActive, hasC0002, isMedalliaEnabled, order, user, userData, memberPoints, optimizelyCampaigns]);

  // Imperative, caller-invoked — not effect-driven, so there is no re-render to guard against
  // (each call is a fresh, deliberate report of a completed action). Gated on the master switch
  // only (not consent-gated, following the MedalliaTrigger precedent).
  const recordMedalliaAction = useCallback((type: AutoshipActionType, success: boolean, options?: RecordMedalliaActionOptions): Promise<void> => new Promise<void>(resolve => {
    if (!isActive) {
      resolve();
      return;
    }

    // `order` is a snapshot from before this action's own refetch — options.orderDate
    // lets the caller (e.g. change_date) supply the value it already has instead of
    // writing the pre-change date into the context.
    const orderForContext = order && options?.orderDate ? {
      ...order,
      place: options.orderDate
    } : order;
    const actionInfo: PetsmartMedalliaSubscriptionActionInfo = {
      actionType: type,
      actionSuccess: success,
      subscriptionId: options?.subscriptionId,
      subscriptionFrequency: options?.subscriptionFrequency,
      cancelReason: options?.cancelReason
    };
    const activeAction = {
      actionInfo,
      order: orderForContext
    };

    // Held until settled so the base sync effect keeps reasserting this action and its
    // order override if it re-runs mid-flight instead of wiping either back to "no
    // action"/the stale pre-action order (see the ref's declaration above).
    activeActionRef.current = activeAction;
    const clearActiveAction = () => {
      if (activeActionRef.current === activeAction) {
        activeActionRef.current = undefined;
      }
    };
    try {
      writeMedalliaContext({
        hasC0002,
        isMedalliaEnabled,
        order: orderForContext,
        user,
        userData,
        memberPoints,
        optimizelyCampaigns,
        actionInfo
      });
    } catch {
      // Best-effort — a context-write throw must not reject the returned promise.
    }
    if (!success || !options?.showSurvey) {
      clearActiveAction();
      resolve();
      return;
    }
    const activeFormId = actionFormIds?.[type] || formId;
    let stopPolling: (() => void) | undefined;
    let sdkLoadTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      stopPolling?.();
      clearTimeout(sdkLoadTimeoutId);
      clearActiveAction();
      resolve();
    };
    const show = () => {
      globalScope.removeEventListener('neb_OnsiteLoaded', show);
      clearTimeout(sdkLoadTimeoutId);
      try {
        const sdk = globalScope.KAMPYLE_ONSITE_SDK;
        if (!sdk) {
          // Timed out (or neb_OnsiteLoaded fired without an SDK) — nothing will close.
          settle();
          return;
        }
        if (activeFormId) {
          sdk.loadForm(activeFormId);
          const shown = sdk.showForm(activeFormId);
          if (shown || sdk.isSurveyDisplayed()) {
            stopPolling = waitForSurveyClose(sdk, settle);
          } else {
            // showForm failed to open a survey — nothing will close, so resolve now.
            settle();
          }
        } else {
          sdk.updatePageView();
          settle();
        }
      } catch {
        // SDK methods (showForm, isSurveyDisplayed) can throw — settle so the
        // caller is never left with a rejected promise after a successful action.
        settle();
      }
    };
    if (globalScope.KAMPYLE_ONSITE_SDK) {
      show();
    } else {
      globalScope.addEventListener('neb_OnsiteLoaded', show, {
        once: true
      });
      sdkLoadTimeoutId = setTimeout(show, SDK_LOAD_TIMEOUT_MS);
    }
  }), [isActive, formId, actionFormIds, hasC0002, isMedalliaEnabled, order, user, userData, memberPoints, optimizelyCampaigns]);
  return {
    recordMedalliaAction
  };
}